import { basicResetReducer, containerReducer, type ContainerState } from '@ackee/redux-utils';
import { createReducer } from '@reduxjs/toolkit';
import { mapValues, uniq } from 'lodash-es';
import { combineReducers, type Reducer } from 'redux';
import { persistReducer } from 'redux-persist';
// @ts-expect-error
import storage from 'redux-persist-indexeddb-storage';

import { authSessionEnd } from '@/modules/auth/services/actions';

import { SECRETS_CACHE, SecretType } from '../../constants';
import type { Secret, SecretById, TOTP } from '../../types';
import {
    deleteSecretsSuccess,
    getSecretTOTPReset,
    getSecretTOTPSuccess,
    ignoreReportSecretSuccess,
    removeSecretSuccess,
    removeSecretTOTP,
    removeTag,
    setSecret,
    setSecrets,
    shareSecretSuccess,
    updateTag,
} from '../actions';
import { omitSensitiveKeys } from '../normalizers';

const initialState: {
    byIds: SecretById;
    ids: Secret['id'][];
} = {
    byIds: {},
    ids: [],
};

const updateTagFn = (tags: string[] | undefined, tag: [string, string]) => tags?.map(t => (t === tag[0] ? tag[1] : t)) ?? undefined;

const removeTagFn = (tags: string[] | undefined, tag: string) => tags?.filter(t => t !== tag) ?? undefined;

const list = createReducer(initialState, builder => {
    builder
        .addCase(setSecrets, (state, action) => {
            const { byIds, ids } = action.payload;

            // Defense-in-depth: readSecret/updateSecret/createSecret sagas dispatch
            // full server responses. Without this, sensitive fields land in IndexedDB.
            const safeByIds = mapValues(byIds, omitSensitiveKeys) as SecretById;

            if (action.meta.replace) {
                state.byIds = safeByIds;
                state.ids = ids;

                return;
            }

            state.byIds = {
                ...state.byIds,
                ...safeByIds,
            };
            state.ids = uniq([...state.ids, ...ids]);
        })
        .addCase(ignoreReportSecretSuccess, (state, action) => {
            const secret = state.byIds[action.meta.id];

            if (secret && secret.type === SecretType.Password) {
                secret.ignoreSecurityReport = true;
            }
        })
        .addCase(shareSecretSuccess, (state, action) => {
            const secret = state.byIds[action.meta.id];

            if (secret) {
                secret.share = action.payload;
            }
        })
        .addCase(removeSecretSuccess, (state, action) => {
            state.ids = state.ids.filter(id => id !== action.meta.id);
        })
        .addCase(deleteSecretsSuccess, () => initialState)
        .addCase(updateTag, (state, action) => {
            const ids = Object.keys(state.byIds);

            ids.forEach(id => {
                state.byIds[id].tags = updateTagFn(state.byIds[id].tags, action.payload.tag);
            });
        })
        .addCase(removeTag, (state, action) => {
            const ids = Object.keys(state.byIds);

            ids.forEach(id => {
                state.byIds[id].tags = removeTagFn(state.byIds[id].tags, action.payload.tag);
            });
        });
});

const resettedList = basicResetReducer(list, authSessionEnd.toString());

const persistedList = persistReducer(
    {
        key: SECRETS_CACHE,
        storage: storage(SECRETS_CACHE),
    },
    resettedList,
);

// @ts-expect-error
const detailInitialState: Secret = null;

const detailChildReducer = createReducer(detailInitialState, builder => {
    builder.addCase(setSecret, (_, action) => ({
        // @ts-expect-error
        id: action.meta.id,
        ...action.payload,
    }));
    // @ts-expect-error
    builder.addCase(ignoreReportSecretSuccess, state => {
        if (state) {
            return {
                ...state,
                ignoreSecurityReport: true,
            };
        }
        return null;
    });
    // @ts-expect-error
    builder.addCase(shareSecretSuccess, (state, action) => {
        if (state) {
            return {
                ...state,
                share: action.payload,
            };
        }
        return null;
    });
    builder.addCase(updateTag, (state, action) => {
        state.tags = updateTagFn(state.tags, action.payload.tag);
    });
    builder.addCase(removeTag, (state, action) => {
        state.tags = removeTagFn(state.tags, action.payload.tag);
    });
});

const detail = containerReducer({
    actionTypes: [setSecret.toString(), ignoreReportSecretSuccess.toString(), shareSecretSuccess.toString()],
    childReducer: detailChildReducer,
    selectors: {
        itemId: action => action.meta.id,
    },
}) as Reducer<ContainerState<Secret>>;

const totpInitialState: TOTP[] = [];

const totpChildReducer = createReducer(totpInitialState, builder => {
    builder.addCase(getSecretTOTPSuccess, (state, action) => [...state, ...action.payload]);
    builder.addCase(getSecretTOTPReset, () => totpInitialState);
    builder.addCase(removeSecretTOTP, (state, action) => state.filter(({ code }) => code !== action.meta.code));
});

const totp = containerReducer({
    actionTypes: [getSecretTOTPSuccess.toString(), getSecretTOTPReset.toString(), removeSecretTOTP.toString()],
    childReducer: totpChildReducer,
    selectors: {
        itemId: action => action.meta.id,
    },
}) as Reducer<ContainerState<TOTP[]>>;

const searchIndexInitialState: Record<Secret['id'], string> = {};

const searchIndex = createReducer(searchIndexInitialState, builder => {
    builder.addCase(setSecrets, (state, action) => {
        const notes: Record<Secret['id'], string> = action.meta.replace ? {} : { ...state };

        for (const [id, secret] of Object.entries(action.payload.byIds)) {
            if (secret.note && secret.type !== SecretType.SecureNote) {
                notes[id] = secret.note;
            } else {
                // A cleared note arrives as '' through the merge branch — drop it,
                // otherwise the record keeps matching a term the user deleted.
                delete notes[id];
            }
        }

        return notes;
    });

    builder.addCase(removeSecretSuccess, (state, action) => {
        delete state[action.meta.id];
    });

    builder.addCase(deleteSecretsSuccess, () => searchIndexInitialState);
});

export const entitiesReducer = combineReducers({
    list: persistedList,
    searchIndex: basicResetReducer(searchIndex, authSessionEnd.toString()),
    detail: basicResetReducer(detail, authSessionEnd.toString()),
    totp: basicResetReducer(totp, authSessionEnd.toString()),
});
